import {
  IconBold,
  IconClearFormatting,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconUnderline,
} from '@tabler/icons';
import { useCurrentEditor, BubbleMenu } from '@tiptap/react';
import { Divider, Group } from '@mantine/core';
import { FC, useCallback } from 'react';
import { EditorActionIcon } from '../../../Atoms/ActionIconWithoutFocus';
import ToolbarHeadingIcon from './icons/ToolbarHeadingIcon';
import ToolbarColorIcon from './icons/ToolbarColorIcon';

const DocumentBubbleMenu: FC<{ includeListOpts?: boolean }> = ({ includeListOpts = false }) => {
  const { editor } = useCurrentEditor();

  const setLink = useCallback(() => {
    if (editor) {
      const previousUrl = editor.getAttributes('link').href;
      // eslint-disable-next-line no-alert
      const url = window.prompt('URL', previousUrl);
      // cancelled
      if (url === null) {
        return;
      }
      // empty
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      // update link
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
          <Group
            spacing={0}
            sx={(theme) => ({
              background: theme.colorScheme === 'dark' ? theme.colors.dark[6] : 'white',
              border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]}`,
              borderRadius: 5,
              boxShadow: 'rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px',
            })}
          >
            <EditorActionIcon
              variant={editor.isActive('bold') ? 'filled' : 'subtle'}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={!editor.can().chain().focus().toggleBold().run()}
            >
              <IconBold size={20} />
            </EditorActionIcon>
            <Divider
              orientation="vertical"
              my={5}
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1] })}
            />
            <EditorActionIcon
              variant={editor.isActive('italic') ? 'filled' : 'subtle'}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editor.can().chain().focus().toggleItalic().run()}
            >
              <IconItalic size={20} />
            </EditorActionIcon>
            <Divider
              orientation="vertical"
              my={5}
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1] })}
            />
            <EditorActionIcon
              variant={editor.isActive('underline') ? 'filled' : 'subtle'}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              disabled={!editor.can().chain().focus().toggleUnderline().run()}
            >
              <IconUnderline size={20} />
            </EditorActionIcon>
            <Divider
              orientation="vertical"
              my={5}
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1] })}
            />
            <ToolbarHeadingIcon includeTooltip={false} />
            <Divider
              orientation="vertical"
              my={5}
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1] })}
            />
            <EditorActionIcon
              variant="subtle"
              onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
              disabled={!editor.can().chain().focus().clearNodes().unsetAllMarks().run()}
            >
              <IconClearFormatting size={20} />
            </EditorActionIcon>
            <Divider
              orientation="vertical"
              my={5}
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1] })}
            />
            <EditorActionIcon variant={editor.isActive('link') ? 'filled' : 'subtle'} onClick={setLink}>
              <IconLink size={20} />
            </EditorActionIcon>
            <Divider
              orientation="vertical"
              my={5}
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1] })}
            />
            {includeListOpts && (
              <>
                <EditorActionIcon
                  variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  disabled={!editor.can().chain().focus().toggleBulletList().run()}
                >
                  <IconList size={20} />
                </EditorActionIcon>
                <Divider
                  orientation="vertical"
                  my={5}
                  sx={(theme) => ({
                    color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
                  })}
                />
                <EditorActionIcon
                  variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  disabled={!editor.can().chain().focus().toggleOrderedList().run()}
                >
                  <IconListNumbers size={20} />
                </EditorActionIcon>
                <Divider
                  orientation="vertical"
                  my={5}
                  sx={(theme) => ({
                    color: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
                  })}
                />
              </>
            )}
            <ToolbarColorIcon includeTooltip={false} />
          </Group>
        </BubbleMenu>
      )}
    </>
  );
};

export default DocumentBubbleMenu;
